import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { authClient } from '../lib/authClient';

/**
 * AuthConfirmPage
 *
 * Handles the email-confirmation callback for sign-up (and similar OTP flows).
 *
 * Supabase normally redirects to this page from `/auth/v1/verify?token=...&type=signup&redirect_to=...`,
 * appending `#access_token=...&refresh_token=...&type=signup` to the URL hash. We consume that hash
 * via `authClient.consumeHashSession()` and route the user to the dashboard.
 *
 * If the Supabase project's "Site URL" is misconfigured (e.g. a wildcard pattern like
 * `https://*.betterchoicefood.com/`), the browser can't navigate to the redirect_to and the user lands
 * here directly with just a `token` (and `type`) query string. In that case we POST to
 * /api/auth/verify-otp to exchange the token for a session, then route to the dashboard.
 */
function AuthConfirmPage() {
  const { isDarkMode, themeClasses } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { session, loading: authLoading } = useAuth();

  const [language] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  // 'verifying' | 'success' | 'error' | 'recovery'
  const [status, setStatus] = useState('verifying');
  const [errorMessage, setErrorMessage] = useState('');

  const translations = {
    english: {
      'confirm.verifying': 'Confirming your email…',
      'confirm.success': 'Email confirmed! Taking you to the dashboard…',
      'confirm.recovery': 'Link verified. Continue to set a new password.',
      'confirm.error.title': 'We couldn’t confirm your email',
      'confirm.error.generic': 'The confirmation link is invalid or has expired.',
      'confirm.error.missingToken': 'This page was opened without a confirmation token.',
      'confirm.continue': 'Continue',
      'confirm.backToLogin': 'Back to sign in',
      'confirm.goToReset': 'Set new password',
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'confirm.verifying': 'מאשר את האימייל שלך…',
      'confirm.success': 'האימייל אושר! מעביר אותך ללוח הבקרה…',
      'confirm.recovery': 'הקישור אומת. המשך לבחירת סיסמה חדשה.',
      'confirm.error.title': 'לא הצלחנו לאמת את האימייל',
      'confirm.error.generic': 'קישור האישור אינו תקין או שפג תוקפו.',
      'confirm.error.missingToken': 'דף זה נפתח ללא טוקן אישור.',
      'confirm.continue': 'המשך',
      'confirm.backToLogin': 'חזרה להתחברות',
      'confirm.goToReset': 'בחר סיסמה חדשה',
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    },
  };
  const t = (key) => translations[language]?.[key] || key;

  // Parse both the URL hash and query string for tokens. Supabase historically used the URL hash
  // for the implicit flow; the new flows (and direct verify URLs) use the query string.
  const parsed = useMemo(() => {
    if (typeof window === 'undefined') return {};
    const result = {
      hasHashSession: false,
      tokenHash: null,
      type: null,
      errorFromUrl: null,
      errorDescription: null,
    };

    const hash = window.location.hash || '';
    if (hash && /access_token=|refresh_token=|type=recovery|type=signup/i.test(hash)) {
      result.hasHashSession = true;
      const hp = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      result.type = hp.get('type') || result.type;
      if (hp.get('error')) result.errorFromUrl = hp.get('error');
      if (hp.get('error_description')) result.errorDescription = hp.get('error_description');
    }

    const qp = new URLSearchParams(location.search || '');
    if (!result.tokenHash) {
      result.tokenHash = qp.get('token_hash') || qp.get('token') || null;
    }
    if (!result.type) result.type = qp.get('type') || null;
    if (!result.errorFromUrl) result.errorFromUrl = qp.get('error') || null;
    if (!result.errorDescription) result.errorDescription = qp.get('error_description') || null;

    return result;
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const finish = (s, msg = '') => {
      if (cancelled) return;
      setStatus(s);
      if (msg) setErrorMessage(msg);
    };

    const clearUrl = () => {
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (_) { /* ignore */ }
    };

    const navigateAfterSession = (type) => {
      if (type === 'recovery') {
        finish('recovery');
        navigate('/reset-password', { replace: true });
        return;
      }
      finish('success');
      clearUrl();
      navigate('/dietitian-dashboard', { replace: true });
    };

    (async () => {
      // 0. If Supabase already returned an error in the URL, surface it.
      if (parsed.errorFromUrl || parsed.errorDescription) {
        finish('error', parsed.errorDescription || parsed.errorFromUrl || t('confirm.error.generic'));
        return;
      }

      // 1. Hash-token flow (standard Supabase implicit redirect after /verify).
      if (parsed.hasHashSession) {
        try {
          const type = authClient.consumeHashSession();
          if (cancelled) return;
          // consumeHashSession is sync; the session is now in localStorage and SIGNED_IN was emitted.
          // Give AuthContext a tick to pick it up before navigating.
          setTimeout(() => navigateAfterSession(type || parsed.type), 50);
          return;
        } catch (err) {
          console.error('consumeHashSession failed:', err);
          finish('error', err?.message || t('confirm.error.generic'));
          return;
        }
      }

      // 2. Already authenticated (e.g. refreshed this page after a successful confirm).
      if (!authLoading && session) {
        navigateAfterSession(parsed.type);
        return;
      }

      // 3. Token-in-query-string flow: hit the backend to verify the OTP.
      if (parsed.tokenHash) {
        const type = parsed.type || 'signup';
        const { data, error } = await authClient.verifyOtp({ token_hash: parsed.tokenHash, type });
        if (cancelled) return;
        if (error) {
          finish('error', error.message || t('confirm.error.generic'));
          return;
        }
        navigateAfterSession(data?.type || type);
        return;
      }

      // 4. Nothing to verify and no session.
      if (!authLoading) {
        finish('error', t('confirm.error.missingToken'));
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, authLoading, session]);

  const Card = ({ children }) => (
    <div className={`${themeClasses.bgCard}  rounded-lg ${themeClasses.shadowCard} p-8 border ${themeClasses.borderPrimary}`}>
      {children}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12 transition-colors duration-300`}
      dir={direction}
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img src="/logo.png" alt="BetterChoice Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className={`text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>{t('brand.name')}</h1>
          <p className={`${isDarkMode ? 'text-forest' : 'text-forest-ondark'} text-lg font-medium`}>{t('brand.portal')}</p>
        </div>

        <Card>
          {status === 'verifying' && (
            <div className="flex flex-col items-center text-center">
              <svg className="animate-spin h-10 w-10 text-forest-ondark mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <p className={`text-base ${themeClasses.textPrimary}`}>{t('confirm.verifying')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center text-center">
              <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm w-full`}>
                {t('confirm.success')}
              </div>
              <button
                type="button"
                onClick={() => navigate('/dietitian-dashboard', { replace: true })}
                className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300  `}
              >
                {t('confirm.continue')}
              </button>
            </div>
          )}

          {status === 'recovery' && (
            <div className="flex flex-col items-center text-center">
              <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm w-full`}>
                {t('confirm.recovery')}
              </div>
              <button
                type="button"
                onClick={() => navigate('/reset-password', { replace: true })}
                className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300  `}
              >
                {t('confirm.goToReset')}
              </button>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center text-center">
              <h2 className={`text-xl font-semibold ${themeClasses.textPrimary} mb-3`}>{t('confirm.error.title')}</h2>
              <div className={`mb-4 p-3 ${isDarkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-100 border-red-300 text-red-700'} border rounded-lg text-sm w-full`}>
                {errorMessage || t('confirm.error.generic')}
              </div>
              <button
                type="button"
                onClick={() => navigate('/', { replace: true })}
                className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300  `}
              >
                {t('confirm.backToLogin')}
              </button>
            </div>
          )}
        </Card>

        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default AuthConfirmPage;
